import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, Check, UserCheck, Video, Calendar, Mail } from "lucide-react";

const POLL_MS = 20000;

const typeIcon = (t) => {
  if (t === "approval_request" || t === "invite_accepted") return UserCheck;
  if (t === "gdo_upload_complete" || t === "game_upload") return Video;
  if (t === "gdo_game_assigned") return Calendar;
  return Mail;
};

const typeColor = (t) => {
  if (t === "approval_request") return "#ff9800";
  if (t === "invite_accepted") return "#ce93d8";
  if (t === "gdo_game_assigned") return "#ce93d8";
  if (t === "gdo_upload_complete" || t === "game_upload") return "#00e5ff";
  return "#4caf50";
};

const NotificationBell = ({ collapsed }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(110);
  const anchorRef = useRef(null);

  const recalcTop = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownTop(Math.max(12, rect.top));
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await api().get("/notifications/unread-count");
      setUnread(r.data?.count ?? 0);
    } catch {}
  }, [api]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get("/notifications");
      setItems(r.data || []);
      setUnread((r.data || []).filter(n => !n.read).length);
    } catch {} finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchUnread]);

  useEffect(() => {
    if (!open) return;
    recalcTop();
    fetchItems();
    const onScroll = () => recalcTop();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, fetchItems, recalcTop]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api().put(`/notifications/${n.id}/read`);
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(c => Math.max(0, c - 1));
    } catch {}
  };

  const onItemClick = async (n) => {
    await markRead(n);
    setOpen(false);
    if (n.type === "approval_request" || n.type === "invite_accepted") navigate("/admin");
    else if (n.type === "game_upload" || n.type === "gdo_upload_complete") navigate("/games");
    else if (n.type === "gdo_game_assigned") navigate("/gdo-portal");
  };

  const markAllRead = async () => {
    const unreadItems = items.filter(i => !i.read);
    if (unreadItems.length === 0) return;
    try {
      await Promise.all(unreadItems.map(i => api().put(`/notifications/${i.id}/read`)));
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch {}
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  };

  return (
    <div ref={anchorRef} style={{ position: 'relative' }}>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: collapsed ? '12px' : '10px 12px',
          borderRadius: 8,
          background: open ? 'rgba(0, 229, 255, 0.08)' : 'transparent',
          color: open ? 'var(--cyan)' : 'var(--text-secondary)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 14,
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 500,
          marginBottom: 4,
          justifyContent: collapsed ? 'center' : 'flex-start',
          position: 'relative',
        }}
      >
        <div style={{ position: 'relative', display: 'flex' }}>
          <Bell size={18} />
          {unread > 0 && (
            <span
              data-testid="notifications-bell-badge"
              style={{
                position: 'absolute',
                top: -4,
                right: -6,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: '#f44336',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </div>
        {!collapsed && <span>Notifications</span>}
      </button>

      {open && (
        <div
          data-testid="notifications-dropdown"
          style={{
            position: 'fixed',
            left: 'auto',
            right: 24,
            top: dropdownTop,
            width: 360,
            maxHeight: '70vh',
            background: '#121216',
            border: '1px solid var(--dark-border)',
            borderRadius: 12,
            boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 200,
          }}
        >
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--dark-border)' }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Notifications</h4>
            {unread > 0 && (
              <button data-testid="notifications-mark-all-read" onClick={markAllRead} style={{ background: 'none', border: 'none', color: 'var(--cyan)', fontSize: 12, cursor: 'pointer', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                Mark all read
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <p style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</p>
            ) : items.length === 0 ? (
              <div data-testid="notifications-empty" style={{ padding: 36, textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Bell size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
                <p style={{ fontSize: 13 }}>You're all caught up.</p>
              </div>
            ) : (
              items.map(n => {
                const Icon = typeIcon(n.type);
                const color = typeColor(n.type);
                return (
                  <button
                    key={n.id}
                    data-testid={`notif-item-${n.id}`}
                    onClick={() => onItemClick(n)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '12px 16px',
                      background: n.read ? 'transparent' : 'rgba(255,152,0,0.04)',
                      border: 'none',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: "'DM Sans', sans-serif",
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(255,152,0,0.04)'; }}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={14} style={{ color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: 'var(--text-primary)', marginBottom: 2 }}>{n.title}</p>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>{n.message}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, opacity: 0.7 }}>{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--cyan)', flexShrink: 0, marginTop: 6 }} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
